import { useState, useMemo } from "react";
import { formatExpiryDate } from "../../utils/formatters";
import BarcodeLabel from "../../components/BarcodeLabel/BarcodeLabel";
import styles from "./BarcodesPage.module.css";

export default function BarcodesPage({ products }) {
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [printing, setPrinting] = useState(false);

  const nobcProducts = useMemo(
    () => products.filter(p => p.barcode && p.barcode.startsWith("NOBC-")),
    [products]
  );

  const filtered = useMemo(() =>
    nobcProducts.filter(p =>
      (!typeFilter || p.type === typeFilter) &&
      (!search ||
        p.description.toLowerCase().includes(search.toLowerCase()) ||
        p.manufacturer.toLowerCase().includes(search.toLowerCase()) ||
        p.barcode.toLowerCase().includes(search.toLowerCase()))
    ),
    [nobcProducts, typeFilter, search]
  );

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(filtered.map(p => p.id)));
  const deselectAll = () => setSelected(new Set());

  const selectedProducts = useMemo(
    () => filtered.filter(p => selected.has(p.id)),
    [filtered, selected]
  );

  const handlePrint = () => {
    setPrinting(true);
    setTimeout(() => {
      window.print();
      setPrinting(false);
    }, 100);
  };

  return (
    <div className={styles.container}>
      {/* Screen view */}
      <div className={styles.screenOnly}>
        <div className={styles.toolbar}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search generated barcodes..."
            className={styles.searchInput}
          />
          <div className={styles.filterGroup}>
            {["", "Drug", "Supply"].map(t => (
              <button key={t} onClick={() => setTypeFilter(t)}
                className={typeFilter === t ? styles.filterBtnActive : styles.filterBtn}>
                {t || "All"}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.actionBar}>
          <div className={styles.countLabel}>
            {filtered.length} PRODUCT{filtered.length !== 1 ? "S" : ""} WITH GENERATED BARCODES
          </div>
          <div className={styles.actions}>
            <button onClick={selectAll} className={styles.actionBtn}>Select All</button>
            <button onClick={deselectAll} className={styles.actionBtn}>Deselect All</button>
            <button
              onClick={handlePrint}
              disabled={selected.size === 0}
              className={selected.size > 0 ? styles.printBtn : styles.printBtnDisabled}
            >
              PRINT SELECTED ({selected.size})
            </button>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className={styles.empty}>
            No manually-entered products found.
          </div>
        ) : (
          <div className={styles.grid}>
            {filtered.map(p => (
              <div key={p.id} className={`${styles.card} ${selected.has(p.id) ? styles.cardSelected : ""}`}
                onClick={() => toggleSelect(p.id)}>
                <div className={styles.checkboxCol}>
                  <input
                    type="checkbox"
                    checked={selected.has(p.id)}
                    onChange={() => toggleSelect(p.id)}
                    className={styles.checkbox}
                    onClick={e => e.stopPropagation()}
                  />
                </div>
                <div className={styles.barcodeCol}>
                  <BarcodeLabel
                    barcode={p.barcode}
                    description={p.description}
                    lotNumber={p.lotNumber}
                    expiryDate={p.expiryDate}
                  />
                </div>
                <div className={styles.detailsCol}>
                  <span className={p.type === "Drug" ? styles.tagDrug : styles.tagSupply}>{p.type}</span>
                  <div className={styles.productDesc}>{p.description}</div>
                  <div className={styles.productMeta}>{p.manufacturer}</div>
                  <div className={styles.productMeta}>
                    Lot: {p.lotNumber}
                    {p.expiryDate && <> &middot; Exp: {formatExpiryDate(p.expiryDate)}</>}
                    {" "}&middot; Stock: {p.stock ?? 0}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Print layout - only rendered when printing */}
      {printing && (
        <div className={styles.printContainer}>
          <div className={styles.labelGrid}>
            {selectedProducts.map(p => (
              <div key={p.id} className={styles.labelCell}>
                <BarcodeLabel
                  barcode={p.barcode}
                  description={p.description}
                  lotNumber={p.lotNumber}
                  expiryDate={p.expiryDate}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
